/**
 * 條件評估器 - 前後端共用模組
 * 
 * @module lib/shared/condition-evaluator
 * @description 提供條件評估的核心邏輯，用於審批流程的條件判斷
 * 
 * 使用場景：
 * - 前端：FormEditor 顯示預期審批流程
 * - 前端：ApproverOverridePanel 根據條件顯示/隱藏步驟
 * - 後端：提交時計算實際審批流程
 * 
 * @example
 * import { evaluateCondition, evaluateConditionGroup } from '@/lib/shared/condition-evaluator';
 * const result = evaluateCondition({ field: 'total_cost', operator: '>=', value: 100 }, formData);
 */

/**
 * 評估單個條件
 * @param {Object} condition - 條件配置
 * @param {string} condition.field - 表單欄位名稱
 * @param {string} condition.operator - 比較運算子
 * @param {*} condition.value - 比較值
 * @param {Object} formData - 表單數據
 * @returns {boolean} 條件是否滿足
 */
export function evaluateCondition(condition, formData) {
    const { field, operator, value } = condition;
    const fieldValue = formData[field];
    
    // 如果字段值不存在，根據操作符返回不同結果
    if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
        // 對於 "等於" 類型的操作，空值不匹配任何非空條件
        if (['equals', 'eq', '==', '==='].includes(operator)) {
            return value === '' || value === null || value === undefined;
        }
        // 對於數值比較，空值視為不滿足條件
        if (['greaterThan', 'gt', '>', 'greaterThanOrEqual', 'gte', '>=', 
             'lessThan', 'lt', '<', 'lessThanOrEqual', 'lte', '<='].includes(operator)) {
            return false;
        }
        // 存在性檢查
        if (['exists', 'isNotEmpty'].includes(operator)) {
            return false;
        }
        if (['notExists', 'isEmpty'].includes(operator)) {
            return true;
        }
        return false;
    }
    
    // 根據操作符進行比較
    switch (operator) {
        // 等於
        case 'equals':
        case 'eq':
        case '==':
        case '===':
            return String(fieldValue) === String(value);
        
        // 不等於
        case 'notEquals':
        case 'ne':
        case '!=':
        case '!==':
            return String(fieldValue) !== String(value);
        
        // 大於
        case 'greaterThan':
        case 'gt':
        case '>':
            return Number(fieldValue) > Number(value);
        
        // 大於等於
        case 'greaterThanOrEqual':
        case 'gte':
        case '>=':
            return Number(fieldValue) >= Number(value);
        
        // 小於
        case 'lessThan':
        case 'lt':
        case '<':
            return Number(fieldValue) < Number(value);
        
        // 小於等於
        case 'lessThanOrEqual':
        case 'lte':
        case '<=':
            return Number(fieldValue) <= Number(value);
        
        // 包含（用於數組或字符串）
        case 'contains':
        case 'includes':
            if (Array.isArray(fieldValue)) {
                return fieldValue.includes(value);
            }
            return String(fieldValue).includes(String(value));
        
        // 不包含
        case 'notContains':
        case 'notIncludes':
            if (Array.isArray(fieldValue)) {
                return !fieldValue.includes(value);
            }
            return !String(fieldValue).includes(String(value));
        
        // 在列表中
        case 'in':
        case 'oneOf':
            if (Array.isArray(value)) {
                return value.includes(fieldValue);
            }
            return false;
        
        // 不在列表中
        case 'notIn':
        case 'not_in':
        case 'notOneOf':
            if (Array.isArray(value)) {
                return !value.includes(fieldValue);
            }
            return true;
        
        // 存在（非空）
        case 'exists':
        case 'isNotEmpty':
            return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
        
        // 不存在（為空）
        case 'notExists':
        case 'isEmpty':
            return fieldValue === undefined || fieldValue === null || fieldValue === '';
        
        // 正則匹配
        case 'matches':
        case 'regex':
            try {
                const regex = new RegExp(value);
                return regex.test(String(fieldValue));
            } catch {
                return false;
            }
        
        // 範圍內 (between)
        case 'between':
            if (Array.isArray(value) && value.length === 2) {
                const num = Number(fieldValue);
                return num >= Number(value[0]) && num <= Number(value[1]);
            }
            return false;
        
        default:
            console.warn(`[ConditionEvaluator] Unknown operator: ${operator}`);
            return false;
    }
}

/**
 * 評估條件組（支持 AND/OR 邏輯）
 * @param {Object} conditionGroup - 條件組配置
 * @param {string} [conditionGroup.logic='AND'] - 邏輯運算子 ('AND' | 'OR')
 * @param {Array} [conditionGroup.rules] - 規則數組
 * @param {Array} [conditionGroup.conditions] - 條件數組（與 rules 互換）
 * @param {Object} formData - 表單數據
 * @returns {boolean} 條件組是否滿足
 */
export function evaluateConditionGroup(conditionGroup, formData) {
    const { logic = 'AND', rules = [], conditions = [] } = conditionGroup;
    
    // 支持 rules 或 conditions 字段
    const conditionList = rules.length > 0 ? rules : conditions;
    
    if (conditionList.length === 0) {
        // 沒有條件時，默認返回 true（步驟啟用）
        return true;
    }
    
    if (logic.toUpperCase() === 'OR') {
        // OR 邏輯：任一條件滿足即可
        return conditionList.some(cond => {
            if (cond.logic || cond.rules || cond.conditions) {
                // 嵌套條件組
                return evaluateConditionGroup(cond, formData);
            }
            return evaluateCondition(cond, formData);
        });
    } else {
        // AND 邏輯：所有條件都必須滿足
        return conditionList.every(cond => {
            if (cond.logic || cond.rules || cond.conditions) {
                // 嵌套條件組
                return evaluateConditionGroup(cond, formData);
            }
            return evaluateCondition(cond, formData);
        });
    }
}

/**
 * 評估步驟條件（多條件之間為 OR 關係）
 * @param {Array} stepConditions - 步驟的條件數組
 * @param {Object} formData - 表單數據
 * @returns {boolean} 步驟是否應該啟用（true = 啟用）
 */
export function evaluateStepConditions(stepConditions, formData) {
    if (!stepConditions || stepConditions.length === 0) {
        // 沒有條件的步驟默認啟用
        return true;
    }
    
    // 步驟的多個條件之間是 OR 關係（任一條件滿足即啟用步驟）
    return stepConditions.some(condition => {
        if (condition.logic || condition.rules || condition.conditions) {
            // 條件組
            return evaluateConditionGroup(condition, formData);
        }
        // 單個條件
        return evaluateCondition(condition, formData);
    });
}

/**
 * 檢查步驟是否應該被跳過
 * @param {Object} step - 步驟配置
 * @param {Array} [step.skipConditions] - 跳過條件數組
 * @param {Array} [step.conditions] - 啟用條件數組
 * @param {Object} formData - 表單數據
 * @returns {{ isSkipped: boolean, skipReason: string|null }} 跳過狀態和原因
 */
export function shouldSkipStep(step, formData) {
    let isSkipped = false;
    let skipReason = null;
    
    // 1. 先檢查 skipConditions - 任一滿足則跳過
    if (step.skipConditions && step.skipConditions.length > 0) {
        const shouldSkip = step.skipConditions.some(cond => 
            evaluateCondition(cond, formData)
        );
        if (shouldSkip) {
            isSkipped = true;
            skipReason = 'skipCondition';
        }
    }

    // 2. 再檢查 conditions - 全部滿足才啟用
    if (!isSkipped && step.conditions && step.conditions.length > 0) {
        const conditionsMet = step.conditions.every(cond =>
            evaluateCondition(cond, formData)
        );
        if (!conditionsMet) {
            isSkipped = true;
            skipReason = 'conditionNotMet';
        }
    }
    
    return { isSkipped, skipReason };
}

/**
 * 計算審批流程（純邏輯，不含審批人解析）
 * @param {Object} routeConfig - 路由配置
 * @param {Array} routeConfig.steps - 步驟數組
 * @param {Object} formData - 表單數據
 * @returns {Object} 計算結果
 */
export function calculateApprovalSteps(routeConfig, formData) {
    if (!routeConfig) {
        return {
            enabledSteps: [],
            skippedSteps: [],
            allSteps: [],
            summary: { totalSteps: 0, enabledCount: 0, skippedCount: 0 }
        };
    }
    
    const steps = routeConfig.steps || routeConfig.approvalFlow || [];
    const enabledSteps = [];
    const skippedSteps = [];
    
    steps.forEach((step, index) => {
        const stepId = step.id || step.stepId || `step-${index}`;
        const stepType = step.type || step.stepType;
        
        // submit 和 completed 步驟總是包含
        if (stepType === 'submit' || stepType === 'completed') {
            enabledSteps.push({
                ...step,
                id: stepId,
                index,
                isConditional: false,
                conditionMet: true,
            });
            return;
        }
        
        // 評估條件
        const { isSkipped, skipReason } = shouldSkipStep(step, formData);
        const isConditional = (step.conditions?.length > 0) || (step.skipConditions?.length > 0);
        
        const stepInfo = {
            ...step,
            id: stepId,
            index,
            isConditional,
            conditionMet: !isSkipped,
            skipReason,
        };
        
        if (isSkipped) {
            skippedSteps.push(stepInfo);
        } else {
            enabledSteps.push(stepInfo);
        }
    });
    
    return {
        enabledSteps,
        skippedSteps,
        allSteps: steps.map((step, index) => ({
            ...step,
            id: step.id || step.stepId || `step-${index}`,
            index,
        })),
        summary: {
            totalSteps: steps.length,
            enabledCount: enabledSteps.length,
            skippedCount: skippedSteps.length,
        },
    };
}

/**
 * 獲取條件的人類可讀描述
 * @param {Object} condition - 條件配置
 * @param {Object} [formData={}] - 表單數據（用於顯示當前值）
 * @param {Object} [fieldLabels={}] - 欄位標籤映射
 * @returns {string} 條件描述
 */
export function getConditionDescription(condition, formData = {}, fieldLabels = {}) {
    // 如果條件有自定義描述，直接返回
    if (condition.description) {
        if (typeof condition.description === 'string') {
            return condition.description;
        }
        // 多語言物件
        return condition.description['zh-TW'] || condition.description.en || '';
    }
    
    // 處理條件組
    if (condition.logic || condition.rules || condition.conditions) {
        const { logic = 'AND', rules = [], conditions: conds = [] } = condition;
        const conditionList = rules.length > 0 ? rules : conds;
        const descriptions = conditionList.map(c => getConditionDescription(c, formData, fieldLabels));
        const connector = logic.toUpperCase() === 'OR' ? ' 或 ' : ' 且 ';
        return descriptions.join(connector);
    }
    
    // 生成單個條件的描述
    const { field, operator, value } = condition;
    const fieldLabel = fieldLabels[field] || field;
    const currentValue = formData[field];
    
    const operatorLabels = {
        'equals': '等於',
        'eq': '等於',
        '==': '等於',
        '===': '等於',
        'notEquals': '不等於',
        'ne': '不等於',
        '!=': '不等於',
        '!==': '不等於',
        'greaterThan': '大於',
        'gt': '大於',
        '>': '大於',
        'greaterThanOrEqual': '大於等於',
        'gte': '大於等於',
        '>=': '大於等於',
        'lessThan': '小於',
        'lt': '小於',
        '<': '小於',
        'lessThanOrEqual': '小於等於',
        'lte': '小於等於',
        '<=': '小於等於',
        'contains': '包含',
        'includes': '包含',
        'in': '在列表中',
        'oneOf': '在列表中',
        'notIn': '不在列表中',
        'not_in': '不在列表中',
        'exists': '已填寫',
        'isNotEmpty': '已填寫',
        'notExists': '未填寫',
        'isEmpty': '未填寫',
    };
    
    const opLabel = operatorLabels[operator] || operator;
    let desc = `${fieldLabel} ${opLabel}`;
    
    if (!['exists', 'isNotEmpty', 'notExists', 'isEmpty'].includes(operator)) {
        desc += ` ${Array.isArray(value) ? value.join(', ') : value}`;
    }
    
    if (currentValue !== undefined) {
        desc += ` (目前: ${currentValue})`;
    }
    
    return desc;
}

// 支援的運算子列表
export const SUPPORTED_OPERATORS = [
    'equals', 'eq', '==', '===',
    'notEquals', 'ne', '!=', '!==',
    'greaterThan', 'gt', '>',
    'greaterThanOrEqual', 'gte', '>=',
    'lessThan', 'lt', '<',
    'lessThanOrEqual', 'lte', '<=',
    'contains', 'includes',
    'notContains', 'notIncludes',
    'in', 'oneOf',
    'notIn', 'not_in', 'notOneOf',
    'exists', 'isNotEmpty',
    'notExists', 'isEmpty',
    'matches', 'regex',
    'between'
];

export default {
    evaluateCondition,
    evaluateConditionGroup,
    evaluateStepConditions,
    shouldSkipStep,
    calculateApprovalSteps,
    getConditionDescription,
    SUPPORTED_OPERATORS
};
